import React, { useState } from 'react';
import { AnyObject, FORM_ERROR } from 'final-form';
import { Button, Col, Row } from 'react-bootstrap';
import FormSpy from '@data-driven-forms/react-form-renderer/dist/cjs/form-spy';
import { FormOptions } from '@data-driven-forms/react-form-renderer/dist/cjs/renderer-context';
import { navigateToHome, navigateToServiceRequest } from '../../redux';
import { selectNext } from '.';
import { useDispatch } from 'react-redux';
import { scrollToTop } from '../../utils';
import Schema from '@data-driven-forms/react-form-renderer/dist/cjs/schema';

interface NextButtonProps extends FormOptions {
  handleNext?: (nextStep: string) => void;
  nextStep: string;
  submitStep: string;
  setSubmitting: (submitting: boolean) => void;
  submitting: boolean;
}

const NextButton = ({
  nextStep,
  submitStep,
  handleNext,
  getState,
  handleSubmit,
  setSubmitting,
  submitting
}: NextButtonProps) => {
  const { hasValidationErrors, validating } = getState();
  const onNext = () => {
    if (submitStep) {
      setSubmitting(true);
      const result = handleSubmit();
      if (result) {
        result
          .then((submitResult) => {
            setSubmitting(false);
            if (!submitResult || !submitResult[FORM_ERROR]) {
              // Success - proceed to next step
              handleNext?.(selectNext(nextStep, getState));
              scrollToTop();
            }
          })
          .catch(() => {
            setSubmitting(false);
            scrollToTop();
          });
      }
    } else {
      handleNext?.(selectNext(nextStep, getState));
      scrollToTop();
    }
  };

  return (
    <Button
      type='button'
      variant='secondary-new'
      className='pe-3 px-3 text-capitalize'
      disabled={hasValidationErrors || validating || submitting}
      onClick={onNext}>
      {!submitStep ? 'continue' : submitting ? 'submitting...' : 'submit'}
      <i className='fa-light fa-circle-arrow-right ms-2' />
    </Button>
  );
};

interface FormWizardActions extends Schema, AnyObject {
  disableBack: boolean;
  formOptions: FormOptions;
  handleNext?: (nextStep: string) => void;
  handlePrev?: () => void;
  autoService?: boolean;
  nextStep?: string;
  submitStep?: string;
}

export const FormWizardActions = ({ formOptions, ...props }: FormWizardActions) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  if (props?.autoService && props?.nextStep == '') {
    formOptions.handleSubmit();
  }
  const handleCancel = () => {
    sessionStorage.setItem('performedServices', '');
    formOptions.reset({});
    dispatch(navigateToHome());
  };
  const handleNextWithSubmitState = (nextStep: string) => {
    if (!nextStep) {
      setSubmitting(true); // Only set to true for final submit
    }
    props.handleNext?.(nextStep);
  };
  const navigateToNewServiceRequest = () => {
    sessionStorage.setItem('performedServices', '');
    formOptions.reset({});
    dispatch(navigateToServiceRequest());
  };
  return (
    <FormSpy subscription={{ valid: true, validating: true, submitting: true, submitError: true }}>
      {({ submitError }) => (
        <>
          {submitting && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(229, 231, 235, 0.75)'
              }}>
              <img src='/images/spinner.gif' alt='Loading...' className='w-20 h-20' />
            </div>
          )}
          <Row className='mt-4'>
            {submitError && <p className='text-danger fs-2'>{submitError}</p>}
            <Col xs={6} className='d-flex justify-content-md-start'>
              {!props.nextStep ? (
                <Button type='button' variant='secondary-new' onClick={navigateToNewServiceRequest}>
                  Start a New Service
                  <i className='fa-light fa-circle-arrow-right ms-2' />
                </Button>
              ) : (
                !props.disableBack && (
                  <Button
                    type='button'
                    variant='outline-secondary'
                    className='text-uppercase'
                    onClick={props.handlePrev}>
                    back
                  </Button>
                )
              )}
            </Col>
            <Col xs={6} className='d-flex justify-content-md-end'>
              {!props.nextStep ? (
                <Button type='button' variant='outline-secondary' onClick={handleCancel}>
                  Close
                </Button>
              ) : (
                <NextButton
                  {...formOptions}
                  nextStep={props.nextStep ?? ''}
                  submitStep={props.submitStep ?? ''}
                  handleNext={handleNextWithSubmitState}
                  setSubmitting={setSubmitting}
                  submitting={submitting}
                />
              )}
            </Col>
          </Row>
        </>
      )}
    </FormSpy>
  );
};
